import type { ShareDocument } from '@escala/domain/share';
import type { MinistryIconKey } from '@escala/domain/types';

import { FONT_WEIGHTS, font } from './canvas';

/**
 * O que o desenho precisa antes de começar: a Plus Jakarta Sans em cada peso (o canvas
 * não espera a fonte da página carregar — sem isso, a primeira exportação sai na fonte
 * do sistema) e as fotos dos avatares.
 */

const SAMPLE = 'Escala ÁÉÍÓÚÃÕÇáéíóúãõç 0123456789';

export async function loadFonts(): Promise<void> {
  const fonts = typeof document !== 'undefined' ? document.fonts : undefined;

  if (!fonts?.load) return;

  await Promise.all(FONT_WEIGHTS.map((weight) => fonts.load(font(weight, 20), SAMPLE).catch(() => [])));
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();

    // Sem CORS a foto "suja" o canvas e o navegador recusa exportar a imagem.
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/** Fotos dos avatares do documento; a que não carrega fica com as iniciais. */
export async function loadPhotos(doc: ShareDocument): Promise<Map<string, CanvasImageSource>> {
  const urls = new Set(
    doc.events.flatMap((e) =>
      e.sections.flatMap((s) => s.rows.flatMap((r) => r.people.flatMap((p) => (p.photo ? [p.photo] : []))))
    )
  );
  const loaded = await Promise.all([...urls].map(async (url) => [url, await loadImage(url)] as const));

  return new Map(loaded.flatMap(([url, image]) => (image ? [[url, image] as const] : [])));
}

/** Gera o SVG (em texto) do ícone de um ministério numa cor; vem de quem chama, que tem os desenhos. */
export type IconSvg = (icon: MinistryIconKey, color: string) => string | null;

/** A chave de um ícone já carregado: o mesmo ícone em outra cor é outra imagem. */
export const iconKey = (icon: MinistryIconKey, color: string) => `${icon}|${color}`;

/** Cor do ícone no chip do ministério do cabeçalho (sobre a cor da organização). */
export const HEADER_ICON_COLOR = '#FFFFFF';

/**
 * Os ícones dos ministérios do documento, como imagens para o canvas: o de cada seção na
 * cor do ministério e o do recorte em branco, para o cabeçalho. O que não carrega fica
 * sem ícone (a pílula volta à bolinha na cor). Sem canvas (jsdom), nada a carregar.
 */
export async function loadIcons(
  doc: ShareDocument,
  svgOf: IconSvg | undefined
): Promise<Map<string, CanvasImageSource>> {
  if (!svgOf || !document.createElement('canvas').getContext('2d')) return new Map();

  const wanted = new Map<string, [MinistryIconKey, string]>();

  for (const section of doc.events.flatMap((e) => e.sections)) {
    wanted.set(iconKey(section.icon, section.color), [section.icon, section.color]);
  }
  if (doc.ministry) {
    wanted.set(iconKey(doc.ministry.icon, HEADER_ICON_COLOR), [doc.ministry.icon, HEADER_ICON_COLOR]);
  }

  const loaded = await Promise.all(
    [...wanted].map(async ([key, [icon, color]]) => {
      const svg = svgOf(icon, color);

      return [
        key,
        svg ? await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`) : null,
      ] as const;
    })
  );

  return new Map(loaded.flatMap(([key, image]) => (image ? [[key, image] as const] : [])));
}
